import { uid } from '../../../../lib/uid';
import { salaryTypeLabel, type SalaryGradeOption, type SalaryTypeValue } from '../../plantilla/plantillaTypes';
import type { PositionListItem, PositionRequest } from '../positionTypes';

/*
  The draft a title's form holds, and the conversions to and from it. Its own module rather
  than beside <PositionFields>, because a file exporting a component and functions loses fast
  refresh for everything in it.
*/

/** The grade picker's pre-choice value. */
export const NO_GRADE = '__none__';

/** One row of a text-line inline table — education or experience. */
export interface TextRow {
  key: string;
  text: string;
}

/** One row of the training inline table. */
export interface TrainingRow {
  key: string;
  text: string;
  hours: number | null;
}

/** One row of the eligibility inline table. Null until the picker is answered. */
export interface EligibilityRow {
  key: string;
  eligibilityId: string | null;
}

/**
 * Everything the catalog asks for one title, as the form holds it.
 *
 * One shape for both forms that write a title — the Positions modal and the plantilla form's
 * "Create new position" step — so the two cannot ask for different things. The grade is one
 * key (`type:grade:step`) because one picker sets all three. Each standard is a list of ROWS,
 * one per line of its inline table.
 */
export interface PositionDraft {
  title: string;
  code: string;
  gradeKey: string;
  description: string;
  education: TextRow[];
  training: TrainingRow[];
  experience: TextRow[];
  /** The eligibilities the title accepts — any one qualifies. No row chosen means it requires none. */
  eligibilities: EligibilityRow[];
  otherExpectations: string;
}

export const blankTextRow = (): TextRow => ({ key: uid(), text: '' });
export const blankTrainingRow = (): TrainingRow => ({ key: uid(), text: '', hours: null });
export const blankEligibilityRow = (): EligibilityRow => ({ key: uid(), eligibilityId: null });

/** Rows from stored lines — never none, because an inline table is never empty. */
function textRows(lines: string[] | undefined): TextRow[] {
  return lines && lines.length > 0 ? lines.map((text) => ({ key: uid(), text })) : [blankTextRow()];
}

/** A blank draft, or one seeded from the title being edited. */
export function positionDraftFrom(editing: PositionListItem | null): PositionDraft {
  const training = editing?.training ?? [];
  const eligibilities = editing?.eligibilities ?? [];

  return {
    title: editing?.title ?? '',
    code: editing?.code ?? '',
    gradeKey:
      editing?.salaryGrade == null
        ? NO_GRADE
        : `${editing.salaryGradeType ?? 0}:${editing.salaryGrade}:${editing.salaryStep ?? 1}`,
    description: editing?.description ?? '',
    education: textRows(editing?.education),
    training:
      training.length > 0
        ? training.map((line) => ({ key: uid(), text: line.text, hours: line.hours ?? null }))
        : [blankTrainingRow()],
    experience: textRows(editing?.experience),
    eligibilities:
      eligibilities.length > 0
        ? eligibilities.map((e) => ({ key: uid(), eligibilityId: e.id }))
        : [blankEligibilityRow()],
    otherExpectations: editing?.otherExpectations ?? '',
  };
}

export function codeIsValid(code: string) {
  return /^[A-Za-z0-9-]+$/.test(code.trim());
}

/** A list of rows with at least one line of text — what each standard requires. */
export function hasLine(rows: { text: string }[]) {
  return rows.some((row) => row.text.trim().length > 0);
}

/** Hours on a line naming no training — a figure about nothing, refused by the server too. */
export function trainingRowInvalid(row: TrainingRow) {
  return row.hours !== null && row.text.trim().length === 0;
}

/** A STAGED eligibility whose typed name was cleared while editing — nothing to create. */
export function eligibilityRowsValid(draft: PositionDraft) {
  return !draft.eligibilities.some((row) => stagedEligibilityName(row.eligibilityId)?.trim() === '');
}

/**
 * Every required field answered and well-formed — title, code, grade, a line of education,
 * training and experience, and no half-typed eligibility. The eligibility itself is OPTIONAL
 * (16 Oct 2026): no row chosen states the title requires none.
 */
export function positionDraftValid(draft: PositionDraft) {
  return (
    draft.title.trim().length > 0 &&
    draft.code.trim().length > 0 &&
    codeIsValid(draft.code) &&
    draft.gradeKey !== NO_GRADE &&
    hasLine(draft.education) &&
    hasLine(draft.training) &&
    !draft.training.some(trainingRowInvalid) &&
    hasLine(draft.experience) &&
    eligibilityRowsValid(draft)
  );
}

/** The grade the draft names, split back into its three parts. Null before one is chosen. */
export function positionDraftGrade(draft: PositionDraft) {
  if (draft.gradeKey === NO_GRADE) return null;

  const [gradeType, grade, step] = draft.gradeKey.split(':').map(Number);

  return { salaryGrade: grade, salaryGradeType: gradeType as SalaryTypeValue, salaryStep: step };
}

/** The lines a set of rows states, trimmed and without the blank trailing row. */
function linesOf(rows: TextRow[]) {
  return rows.map((row) => row.text.trim()).filter((text) => text.length > 0);
}

/** Only ever called once `positionDraftValid` holds, so the grade is chosen. */
export function positionRequestFrom(draft: PositionDraft): PositionRequest {
  const eligibilityIds = draft.eligibilities.flatMap((row) => (row.eligibilityId === null ? [] : [row.eligibilityId]));

  return {
    title: draft.title.trim(),
    code: draft.code.trim(),
    description: draft.description.trim() || null,
    education: linesOf(draft.education),
    training: draft.training
      .filter((row) => row.text.trim().length > 0)
      .map((row) => ({ text: row.text.trim(), hours: row.hours })),
    experience: linesOf(draft.experience),
    // No eligibility listed IS the answer "requires none" — the table is optional.
    requiresEligibility: eligibilityIds.length > 0,
    eligibilityIds,
    otherExpectations: draft.otherExpectations.trim() || null,
    // Non-null: positionDraftValid has refused a draft with no grade.
    ...positionDraftGrade(draft)!,
  };
}

/** A grade and step named in words — `SG 11 - Step 3` (product call, 16 Oct 2026). */
export function gradeLabel(grade: number, step: number) {
  return `SG ${grade} - Step ${step}`;
}

/**
 * The grade picker's options, from the published schedule — in NUMBER order (type, then grade,
 * then step), never the label's alphabetical one, which put SG 30 before SG 4. Each carries its
 * type (shown under the label) and what it currently pays (shown at the outer right).
 */
export function gradeOptionsFor(grades: SalaryGradeOption[]) {
  return [...grades]
    .sort((a, b) => a.type - b.type || a.grade - b.grade || a.step - b.step)
    .map((g) => ({
      value: `${g.type}:${g.grade}:${g.step}`,
      label: gradeLabel(g.grade, g.step),
      sublabel: salaryTypeLabel(g.type),
      amount: g.currentMonthlyAmount as number | null,
    }));
}

/**
 * The eligibilities for Autogenerate, from the LIVE catalog — none, or one or two of the rows
 * the form loaded, never an invented id the server would refuse.
 */
export function sampleEligibilities(eligibilities: { id: string }[]): Pick<PositionDraft, 'eligibilities'> {
  if (eligibilities.length === 0 || Math.random() < 0.3) {
    return { eligibilities: [blankEligibilityRow()] };
  }

  const picked = [...eligibilities].sort(() => Math.random() - 0.5).slice(0, Math.min(2, eligibilities.length));

  return { eligibilities: picked.map((e) => ({ key: uid(), eligibilityId: e.id })) };
}

/** Autogenerate's lines as inline-table rows — a standard is a LIST, so the sample fills rows. */
export function qualificationRowsFrom(sample: {
  education: string[];
  training: { text: string; hours: number | null }[];
  experience: string[];
}): Pick<PositionDraft, 'education' | 'training' | 'experience'> {
  return {
    education: sample.education.map((text) => ({ key: uid(), text })),
    training: sample.training.map((line) => ({ key: uid(), text: line.text, hours: line.hours })),
    experience: sample.experience.map((text) => ({ key: uid(), text })),
  };
}

/** `eligibilityIds[2]` → `eligibility-ids[2]` — the same kebab `focusValidationError` applies. */
function kebab(field: string) {
  return field.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();
}

/**
 * Where each field the server refused lives in `<PositionFields>`, for `focusValidationError`.
 *
 * Built from the refusal's own keys because a ROW is named by index (`education[2]`) and no
 * static map can list every row. `serverPrefix` is the request path the title sits under —
 * `newPosition.` on the plantilla form, nothing on the Positions form. A refusal of a whole
 * list points at its first row; the three salary fields share the one grade picker; the
 * eligibility answer is a radio group, which puts its id on each OPTION.
 */
export function positionFieldIds(idPrefix: string, refusedFields: string[], serverPrefix = '') {
  const ids: Record<string, string> = {};

  const special: Record<string, string> = {
    salaryGrade: 'grade',
    salaryStep: 'grade',
    salaryGradeType: 'grade',
    education: 'education[0]',
    experience: 'experience[0]',
    training: 'training[0].text',
    eligibilityIds: 'eligibility-ids[0]',
    requiresEligibility: 'eligibility-ids[0]',
  };

  for (const field of refusedFields) {
    if (!field.startsWith(serverPrefix)) continue;

    const own = field.slice(serverPrefix.length);
    ids[field] = `${idPrefix}-${special[own] ?? kebab(own)}`;
  }

  return ids;
}

/**
 * A row's eligibility that was TYPED into its picker and not written yet — the prefix plus the
 * name, verbatim. Keyed on the NAME, so two rows that typed one word stage one eligibility.
 */
export const STAGED_ELIGIBILITY = '__new__:';

export function stagedEligibilityName(value: string | null) {
  return value !== null && value.startsWith(STAGED_ELIGIBILITY) ? value.slice(STAGED_ELIGIBILITY.length) : null;
}

/**
 * Writes the eligibilities the draft STAGED, and returns the draft with their real ids.
 *
 * Called by the form's save, immediately before the position write — never on the picker's
 * press, which left junk rows from forms that were abandoned. It stays a SEPARATE call under
 * the catalog's own key: folding it into the position request would be one key silently
 * performing a write another governs. Residual gap: a position refused after this leaves the
 * new eligibilities behind — one request wide, not the whole time the form was open. The
 * catalog's create is idempotent on the name, so a retry does not duplicate them.
 */
export async function resolveStagedEligibilities(
  draft: PositionDraft,
  create: (name: string) => Promise<string>,
): Promise<PositionDraft> {
  const names = [...new Set(draft.eligibilities.flatMap((row) => stagedEligibilityName(row.eligibilityId) ?? []))];

  if (names.length === 0) return draft;

  const ids = new Map<string, string>();

  for (const name of names) {
    ids.set(name, await create(name));
  }

  return {
    ...draft,
    eligibilities: draft.eligibilities.map((row) => {
      const name = stagedEligibilityName(row.eligibilityId);
      return name === null ? row : { ...row, eligibilityId: ids.get(name) ?? null };
    }),
  };
}
